import { all, call, put, takeEvery, take } from 'redux-saga/effects'

import { handleError } from 'api/api-utils'
import reportApi from 'api/ReportApi'

import {
  getReportDataError,
  getReportDataSuccess,
  invalidateReportDataCacheError,
  invalidateReportDataCacheSuccess,
  getReportProgressSuccess,
} from './actions'
import { GET_REPORT_DATA, INVALIDATE_REPORT_DATA_CACHE } from './constants'
import { eventChannel, END } from 'redux-saga'
import { tryParseErrorJson } from 'utils/parseError'

// Individual exports for testing
let payloadCache = []
export function* getReportData(action) {
  const { companyCode, customerCode, enableCaching, reportId } = action
  const cacheKey = reportId

  if (payloadCache.includes(cacheKey)) return
  try {
    payloadCache = [...payloadCache, cacheKey]
    const channel = eventChannel((eventEmmit) => {
      let reportRows = []
      let metadata = null

      const onMessage = (event) => {
        // Skip empty events (keepalive messages etc.)
        if (!event.data || event.data.trim() === '') {
          return
        }

        try {
          const parsed = JSON.parse(event.data)

          // Handle metadata
          if (event.event === 'metadata') {
            metadata = parsed
            return
          }

          // Handle data batches
          if (event.event === 'data-batch') {
            // Backend double-serializes: parse the inner JSON string containing the batch array
            const batchItems = JSON.parse(parsed.items)
            reportRows.push(...batchItems) // Spread to add all items from batch
            return
          }

          // Handle completion
          if (event.event === 'data-complete') {
            eventEmmit(
              getReportDataSuccess({
                report: reportRows,
                calculated: metadata?.Metadata?.calculatedUTC,
                exchangeRates: metadata?.metadata?.exchangeRates,
                reportId,
                companyCode,
              })
            )
            reportRows = []
            metadata = null
            eventEmmit(END)
            return
          }

          // Fallback for old non-streaming format (backward compatibility)
          if (parsed.data) {
            eventEmmit(
              getReportDataSuccess({
                report: parsed.data,
                calculated: parsed.metadata?.calculatedUTC,
                exchangeRates: parsed.metadata?.exchangeRates,
                reportId,
                companyCode,
              })
            )
            eventEmmit(END)
          }
        } catch (error) {
          eventEmmit(handleError(error, getReportDataError, { reportId }))
          eventEmmit(END)
        }
      }

      const onProgress = (event) => {
        eventEmmit(
          getReportProgressSuccess({ progress: event.data, ...action })
        )
      }

      const onError = (event) => {
        eventEmmit(
          handleError(tryParseErrorJson(event.data), getReportDataError, {
            reportId,
          })
        )
        eventEmmit(END)
      }

      reportApi.getReport({
        companyCode,
        customerCode,
        enableCaching,
        reportId,
        onMessage,
        onProgress,
        onError,
      })

      return () => {}
    })
    while (true) {
      const event = yield take(channel)
      if (event === END) break
      yield put(event)
    }
  } catch (error) {
    yield put(handleError(error, getReportDataError, { reportId }))
  } finally {
    payloadCache = payloadCache.filter((key) => key !== cacheKey)
  }
}

export function* invalidateReportDataCache(action) {
  try {
    const { reportId } = action
    yield call(reportApi.invalidateReport, { reportId })
    yield put(invalidateReportDataCacheSuccess({ reportId }))
  } catch (error) {
    yield put(handleError(error, invalidateReportDataCacheError))
  }
}

// All sagas to be loaded
export function* reportDatasSaga() {
  yield all([
    takeEvery(GET_REPORT_DATA, getReportData),
    takeEvery(INVALIDATE_REPORT_DATA_CACHE, invalidateReportDataCache),
  ])
}

export default reportDatasSaga
